import { useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useUnit } from 'effector-react';
import { $accountByIdStore, $currencyStore, $initialAccountsStore, $profileFeatureStore } from '@lib/store';
import { AccountResponse, AccountType } from '@shared/api/rest/accounts';
import { AccountTileBig } from '@pages';
import { ManagersBlock } from './widgets/managersBlock';
import { CardsTable } from '@widgets/card';
import { List as Transactions } from '@widgets/transaction';
import { SidebarSection } from '@widgets/card/sidebarSection';
import { Details } from '@widgets';
import { NoData, PageContainer, Spinner, Text } from '@shared/widgets';
import { AccountSidebar } from './subPages';

export const Account = () => {
  const params = useParams();
  const id = useMemo(() => (params.id ? parseInt(params.id) : undefined), [params]);

  const profileFeatureStore = useUnit($profileFeatureStore.store);
  const accountDetailsStore = useUnit($accountByIdStore.store);
  const accountStore = useUnit($initialAccountsStore.store);

  const [mainAccount, setMainAccount] = useState<AccountResponse | undefined>(undefined);
  const [rightSideWidth, setRightSideWidth] = useState(0);
  const [selectedAccountId, setSelectedAccountId] = useState<number | undefined>(id);
  const rightSideRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    $currencyStore.events.update();
  }, []);

  useEffect(() => {
    const newMainAccount =
      accountDetailsStore.data?.mainAccId === null
        ? accountDetailsStore.data
        : accountStore.data.find((a) => a.id === accountDetailsStore.data?.mainAccId);

    setMainAccount(newMainAccount);
  }, [accountDetailsStore.data, accountStore.data]);

  const subAccounts = useMemo(() => {
    if (!mainAccount) {
      return accountStore.data.filter((a) => a.mainAccId != null);
    }
    return accountStore.data.filter((a) => a.mainAccId === mainAccount.id);
  }, [mainAccount, accountStore.data]);

  const handleAccountSwitch = (accountId: number) => {
    setSelectedAccountId(accountId);
  };

  useEffect(() => {
    const newWidth = rightSideRef.current?.scrollWidth ?? 0;
    if (newWidth === 0) return;
    setRightSideWidth(newWidth + 40);
  }, [accountDetailsStore]);

  useEffect(() => {
    if (!params.id) return;
    $accountByIdStore.events.get(selectedAccountId);

    return () => {
      $accountByIdStore.events.clear();
    };
  }, [selectedAccountId]);

  const accountIds = useMemo(() => [selectedAccountId!], [selectedAccountId]);

  return (
    <PageContainer title='Account' backText='Back to all accounts'>
      {!accountDetailsStore.loading && !accountDetailsStore.data ? (
        <NoData />
      ) : accountDetailsStore.data == null ? (
        <Spinner />
      ) : (
        <div className='w-full flex flex-row gap-6'>
          <div className='flex flex-col gap-6' style={{ width: `calc(100% - ${rightSideWidth}px)` }}>
            <AccountTileBig
              account={accountDetailsStore.data}
              mainAccount={mainAccount}
              subAccounts={subAccounts}
              selectedAccountId={selectedAccountId}
              onAccountSwitch={handleAccountSwitch}
            />
            {profileFeatureStore.data?.userManagement?.viewUsers && <ManagersBlock accountId={selectedAccountId!} />}
            {accountDetailsStore.data.type === AccountType.Card && (
              <div className='flex flex-col gap-6'>
                <Text weight='semibold' size='2xl'>
                  Related Cards
                </Text>
                <CardsTable listType={false} accountId={selectedAccountId} />
              </div>
            )}
            {accountDetailsStore?.data && (
              <div className='flex flex-col gap-6'>
                <div className='font-semibold text-2xl mb-0 pb-0'>Transactions</div>
                <Transactions accountIds={accountIds} accountType={accountDetailsStore?.data?.type} filter />
              </div>
            )}
          </div>
          <div ref={rightSideRef} className='flex h-fit min-w-72'>
            {accountDetailsStore.data.type === AccountType.Card ? (
              <AccountSidebar />
            ) : (
              <SidebarSection caption='Account details'>
                <Details />
              </SidebarSection>
            )}
          </div>
        </div>
      )}
    </PageContainer>
  );
};
